import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { Lock, ArrowRight, X, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PremiumGateContext = createContext({ openGate: () => {} });

export function usePremiumGate() {
  return useContext(PremiumGateContext);
}

export function PremiumGateProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  const openGate = useCallback((msg) => {
    setMessage(msg || null);
    setOpen(true);
  }, []);

  // Escuta custom event disparado pelo interceptor axios global
  useEffect(() => {
    const handler = (ev) => openGate(ev?.detail?.message);
    window.addEventListener("liventy:premium-gate", handler);
    return () => window.removeEventListener("liventy:premium-gate", handler);
  }, [openGate]);

  const close = () => setOpen(false);
  const goCheckout = () => {
    setOpen(false);
    navigate("/upgrade");
  };

  return (
    <PremiumGateContext.Provider value={{ openGate }}>
      {children}
      {open && (
        <div
          className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-4 bg-black/55"
          style={{ backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
          onClick={close}
          data-testid="premium-gate-overlay"
        >
          <div
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 sm:p-8"
            style={{ fontFamily: "'Poppins', sans-serif" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="premium-gate-modal"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10.5px] font-semibold text-white"
                style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)" }}>
                <Lock size={11} /> Recurso premium
              </div>
              <button onClick={close} className="w-9 h-9 -mr-2 -mt-2 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center text-zinc-500" data-testid="premium-gate-close" aria-label="Fechar">
                <X size={16} />
              </button>
            </div>
            <h3 className="text-[22px] sm:text-[26px] font-semibold tracking-tight text-zinc-900 leading-tight mt-1">
              Seu período premium terminou
            </h3>
            <p className="text-[13.5px] text-zinc-500 mt-2 leading-relaxed">
              {message || "Assine para continuar adicionando convidados, gerenciando eventos e utilizando todos os recursos da Liventy."}
            </p>

            <div className="mt-5 grid grid-cols-1 gap-1.5 text-[12.5px] text-zinc-700">
              {[
                "Eventos e convidados ilimitados",
                "Automações WhatsApp",
                "Check-in tempo real + QR Code",
                "Financeiro e relatórios completos",
              ].map((it, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Sparkles size={12} className="text-violet-500" />
                  <span>{it}</span>
                </div>
              ))}
            </div>

            <button
              onClick={goCheckout}
              className="w-full mt-6 h-12 rounded-full text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
              style={{
                backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)",
                boxShadow: "0 12px 32px -10px rgba(107,61,246,0.45)",
              }}
              data-testid="premium-gate-cta"
            >
              Continuar assinatura
              <ArrowRight size={15} />
            </button>
            <button onClick={close} className="w-full mt-2 h-10 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900" data-testid="premium-gate-dismiss">
              Continuar navegando em modo leitura
            </button>
          </div>
        </div>
      )}
    </PremiumGateContext.Provider>
  );
}
